import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SrBox from 'client/components/SrBox';
import SrCard, {
  SR_CARD_BULLET_COLOR_PURPLE,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
} from 'client/components/SrCard/SrCard';
import SrTable, { SR_TABLE_THEME_PARTNER_CLEAR } from 'client/components/SrTable';
import SrText from 'client/components/SrText';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_HEIGHT_SHORT,
} from 'client/components/SrButton/SrButton';
import UPProviderSearchForm from 'client/components/UPProviderSearchForm/UPProviderSearchForm';
import UpEditProviderNoteModal from 'client/components/UpEditProviderNoteModal/UpEditProviderNoteModal';
import {
  SR_API_ACTION_UTILITY_PARTNER_PROVIDER_NOTES_GET_LIST,
  SR_API_ACTION_UTILITY_PARTNER_PROVIDER_NOTES_CREATE,
  SR_API_ACTION_UTILITY_PARTNER_PROVIDER_NOTES_UPDATE,
  SR_API_ACTION_UTILITY_PARTNER_PROVIDER_NOTES_DELETE,
} from 'api/utilityActionTypes';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import useDispatch from 'client/hooks/useDispatch';
import { isHomebuyerPartner } from 'common/utility/helpers';

const SrUtilityPartnerProviderNotesTable = ({ utilityPartner }) => {
  const dispatch = useDispatch();
  const [notes, setNotes] = useState([]);
  const [editingNote, setEditingNote] = useState(null);
  const [creatingNote, setCreatingNote] = useState(false);

  const loadNotes = () => {
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_PROVIDER_NOTES_GET_LIST,
      payload: { utilityPartnerId: utilityPartner.id },
    }).then(({ notes }) => {
      setNotes(notes);
    });
  };

  useEffect(() => {
    loadNotes();
  }, [utilityPartner.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const onProviderNoteCancel = () => {
    setEditingNote(null);
  };

  const onProviderNoteSubmit = ({ note }) => {
    let promise = Promise.resolve();
    if (editingNote.id) {
      promise = dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_PARTNER_PROVIDER_NOTES_UPDATE,
        payload: {
          id: editingNote.id,
          text: note,
        },
      }).then(onProviderNoteCancel);
    } else {
      promise = dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_PARTNER_PROVIDER_NOTES_CREATE,
        payload: {
          utilityPartnerId: utilityPartner.id,
          utilityProviderId: editingNote.provider?.id,
          text: note,
        },
      }).then(onProviderNoteCancel);
    }
    promise.then(loadNotes);
  };

  const onProviderNoteDelete = () => {
    if (!global.confirm('Are you sure you want to delete this note?')) {
      return;
    }
    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_PROVIDER_NOTES_DELETE,
      payload: { id: editingNote.id },
    })
      .then(loadNotes)
      .then(onProviderNoteCancel);
  };

  const onProviderSelected = (provider) => {
    setCreatingNote(false);
    setEditingNote({
      id: null,
      note: '',
      provider,
    });
  };

  return (
    <div className="SrUtilityPartnerProviderNotesTable">
      <SrBox marginBottom="100px">
        <SrCard
          color={SR_CARD_BULLET_COLOR_PURPLE}
          contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
          header="Notes per Utility Company"
        >
          <SrBox position="relative" marginTop="40px">
            <SrBox position="absolute" top="-75px" right="0">
              <SrBox width="215px">
                <SrButton
                  theme={SR_BUTTON_THEME_ADOBE_GREEN}
                  heightType={SR_BUTTON_HEIGHT_SHORT}
                  onClick={() => setCreatingNote(true)}
                >
                  + Add Default Note
                </SrButton>
              </SrBox>
            </SrBox>
            <SrTable
              theme={SR_TABLE_THEME_PARTNER_CLEAR}
              headers={[
                { content: 'Utility Company', style: { width: '35%' } },
                { content: 'Default Note', style: { width: '35%' } },
                { content: 'Addresses with Note', style: { width: '15%' } },
                { content: '', style: { width: '15%' } },
              ]}
              rows={notes.map((note) => ({
                cells: [
                  { content: note.provider?.title || 'All Providers' },
                  {
                    content: (
                      <SrText font="greycliff" weight="300" fontSize="16px">
                        “{note.text}”
                      </SrText>
                    ),
                    style: {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '0',
                    },
                  },
                  {
                    content: (
                      <SrBox textAlign="center">
                        <SrText font="greycliff" weight="300" fontSize="16px">
                          {note.provider ? note.addressesWithNote || 'N/A' : 'All Addresses'}
                        </SrText>
                      </SrBox>
                    ),
                  },
                  {
                    content: (
                      <SrText
                        font="greycliff"
                        weight="700"
                        color="blue300"
                        cursor="pointer"
                        onClick={() => setEditingNote(note)}
                      >
                        Edit
                      </SrText>
                    ),
                  },
                ],
              }))}
              minWidth="100%"
            />
            {Array.isArray(notes) && notes.length === 0 && (
              <SrBox backgroundColor="white" display="flex" justifyContent="center" padding="20px">
                <SrText font="greycliff" weight="500" fontSize="16px" fontStyle="italic">
                  You have not added any default notes yet.
                </SrText>
              </SrBox>
            )}
          </SrBox>
        </SrCard>
      </SrBox>
      {editingNote && (
        <UpEditProviderNoteModal
          partnerId={utilityPartner.id}
          utilityPartner={utilityPartner}
          isNew={!editingNote.id}
          defaultNote={editingNote.text}
          provider={editingNote.provider}
          onSubmit={onProviderNoteSubmit}
          onCancel={onProviderNoteCancel}
          onDelete={onProviderNoteDelete}
        />
      )}
      {creatingNote && (
        <UPProviderSearchForm
          title="Add Default Note"
          addressDisplay={
            <SrBox padding="0 20px">
              <SrText font="greycliff" weight="400" lineHeight="25px" fontSize="16px">
                {isHomebuyerPartner(utilityPartner)
                  ? 'This default note will be displayed to clients who use the selected utility company below'
                  : 'This default note will be displayed to tenants who use the selected utility company below'}
              </SrText>
            </SrBox>
          }
          onClose={() => setCreatingNote(false)}
          onProviderSelected={onProviderSelected}
        />
      )}
    </div>
  );
};

SrUtilityPartnerProviderNotesTable.propTypes = {
  utilityPartner: PropTypes.object.isRequired,
};

export default SrUtilityPartnerProviderNotesTable;
